/**
 * Order Management Page - Comprehensive order view and management
 * Displays all orders across brands with advanced filtering and actions
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  InlineGrid,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  IndexTable,
  useIndexResourceState,
  Tooltip,
  Modal,
  Divider,
  Filters,
  ChoiceList,
  RangeSlider,
  TextField,
  DatePicker,
  Popover,
} from '@shopify/polaris';
import {
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  OrderIcon,
  ViewIcon,
  ExternalIcon,
  CashDollarIcon,
  CalendarIcon,
} from '@shopify/polaris-icons';

import { useBrands, useAllBrandOrders, useMarketplacesList } from '../utils/queries';
import apiClient from '../utils/api';

// Helper function to format date as YYYY-MM-DD without timezone shift
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function OrderManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortDirection, setSortDirection] = useState('descending');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetailsModalActive, setOrderDetailsModalActive] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState([]);
  const [marketplaceFilter, setMarketplaceFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [amountRange, setAmountRange] = useState([0, 5000]);

  // Date range filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [datePickerActive, setDatePickerActive] = useState(false);
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date()
  });

  const itemsPerPage = 10;

  // Debounce search query to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build query parameters for API
  const queryParams = useMemo(() => ({
    page: currentPage,
    limit: itemsPerPage,
    sortBy,
    sortDirection,
    search: debouncedSearchQuery || undefined,
    statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
    marketplace: marketplaceFilter.length > 0 ? marketplaceFilter : undefined,
    brandId: brandFilter.length > 0 ? brandFilter : undefined,
    amountMin: amountRange[0] > 0 ? amountRange[0] : undefined,
    amountMax: amountRange[1] < 5000 ? amountRange[1] : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }), [currentPage, sortBy, sortDirection, debouncedSearchQuery, statusFilter, marketplaceFilter, brandFilter, amountRange, startDate, endDate]);

  // Query hooks
  const { data: brandsResponse, isLoading: brandsLoading } = useBrands();
  const brands = brandsResponse?.brands || brandsResponse || [];
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders
  } = useAllBrandOrders(queryParams);

  // Fetch all marketplaces for filter (independent of current page orders)
  const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList('all');

  // Extract orders and pagination from response
  const allOrders = ordersData?.orders || [];

  // Separate summary row from regular orders
  const summaryRow = allOrders.find(order => order.isSummaryRow);
  const orders = allOrders.filter(order => !order.isSummaryRow);

  const pagination = ordersData?.pagination || {};
  const summary = ordersData?.summary || null;
  const totalItems = pagination.totalOrders || 0;
  const totalPages = pagination.totalPages || 0;

  // Get all marketplaces from dedicated API endpoint (not from current page orders)
  const marketplaceOptions = useMemo(() => {
    const marketplaces = marketplacesData?.marketplaces || [];
    return marketplaces.map(mp => ({
      label: mp,
      value: mp
    }));
  }, [marketplacesData]);

  // Brand options for filter
  const brandOptions = useMemo(() => {
    return brands.map(brand => ({
      label: brand.name,
      value: brand.id
    }));
  }, [brands]);

  const resourceName = {
    singular: 'order',
    plural: 'orders',
  };

  // Date picker handlers
  const handleMonthChange = useCallback((month, year) => {
    setSelectedDates({
      start: new Date(year, month, 1),
      end: new Date(year, month + 1, 0)
    });
  }, []);

  const handleDateSelection = useCallback(({start, end}) => {
    setSelectedDates({start, end});
    if (start) {
      setStartDate(formatDateLocal(start));
    }
    if (end) {
      setEndDate(formatDateLocal(end));
    }
  }, []);

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(orders);

  // Filter options
  const statusOptions = [
    { label: 'Paid', value: 'PAID' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Refunded', value: 'REFUNDED' },
    { label: 'Partially Refunded', value: 'PARTIALLY_REFUNDED' },
    { label: 'Fulfilled', value: 'FULFILLED' },
  ];

  // Applied filters
  const appliedFilters = [];
  if (searchQuery) {
    appliedFilters.push({
      key: 'search',
      label: `Search: ${searchQuery}`,
      onRemove: () => setSearchQuery(''),
    });
  }
  if (statusFilter.length > 0) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${statusFilter.join(', ')}`,
      onRemove: () => setStatusFilter([]),
    });
  }
  if (marketplaceFilter.length > 0) {
    appliedFilters.push({
      key: 'marketplace',
      label: `Marketplace: ${marketplaceFilter.join(', ')}`,
      onRemove: () => setMarketplaceFilter([]),
    });
  }
  if (brandFilter.length > 0) {
    const brandLabels = brandFilter.map(id => 
      brands.find(b => b.id === id)?.name || id
    ).join(', ');
    appliedFilters.push({
      key: 'brand',
      label: `Brand: ${brandLabels}`,
      onRemove: () => setBrandFilter([]),
    });
  }
  if (amountRange[0] > 0 || amountRange[1] < 5000) {
    appliedFilters.push({
      key: 'amount',
      label: `Amount: $${amountRange[0]} - $${amountRange[1]}`,
      onRemove: () => setAmountRange([0, 5000]),
    });
  }
  if (startDate || endDate) {
    const dateLabel = startDate && endDate
      ? `Date: ${startDate} to ${endDate}`
      : startDate
        ? `Date: From ${startDate}`
        : `Date: Until ${endDate}`;
    appliedFilters.push({
      key: 'dateRange',
      label: dateLabel,
      onRemove: () => {
        setStartDate('');
        setEndDate('');
      },
    });
  }

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={statusOptions}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'marketplace',
      label: 'Marketplace',
      filter: (
        <ChoiceList
          title="Marketplace"
          titleHidden
          choices={marketplaceOptions}
          selected={marketplaceFilter}
          onChange={setMarketplaceFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'brand',
      label: 'Brand',
      filter: (
        <ChoiceList
          title="Brand"
          titleHidden
          choices={brandOptions}
          selected={brandFilter}
          onChange={setBrandFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'amount',
      label: 'Amount Range',
      filter: (
        <RangeSlider
          label="Net Amount Range"
          value={amountRange}
          onChange={setAmountRange}
          output
          min={0}
          max={5000}
          step={50}
          prefix="$"
        />
      ),
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      filter: (
        <BlockStack gap="300">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={setStartDate}
            autoComplete="off"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={setEndDate}
            autoComplete="off"
            min={startDate}
          />
          <Button
            size="slim"
            onClick={() => {
              const today = new Date();
              const thirtyDaysAgo = new Date(today);
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setStartDate(formatDateLocal(thirtyDaysAgo));
              setEndDate(formatDateLocal(today));
            }}
          >
            Last 30 days
          </Button>
        </BlockStack>
      ),
    },
  ];

  // Clear all filters
  const handleFiltersClearAll = useCallback(() => {
    setSearchQuery('');
    setStatusFilter([]);
    setMarketplaceFilter([]);
    setBrandFilter([]);
    setAmountRange([0, 5000]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  }, []);

  // Handle sort change - updated for new column order
  // Standardized columns: Removed Discount. Added Items. Renamed Gross Revenue → Sales
  const handleSort = useCallback((headingIndex, direction) => {
    const sortFields = [
      'orderNumber',           // 0: Order
      'orderDate',             // 1: Date
      'brandId',               // 2: Brand
      'marketplace',           // 3: Marketplace
      'financialStatus',       // 4: Status
      'totalWeight',           // 5: Weight
      'totalItems',            // 6: Items (NEW)
      'grossAmount',           // 7: Sales (renamed from Gross Revenue)
      'refundAmount',          // 8: Refund
      'netAmount',             // 9: Net Sales
      'appliedCommissionRate', // 10: Commission
      'appliedShippingFee',    // 11: Shipping Fees
      'appliedReturnFee',      // 12: Return Fees
      'payout',                // 13: Net Payout
    ];
    setSortBy(sortFields[headingIndex]);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  // Order details modal
  const selectedOrder = orders.find(order => order.id === selectedOrderId);

  const handleOrderSelect = useCallback((orderId) => {
    setSelectedOrderId(orderId);
    setOrderDetailsModalActive(true);
  }, []);


  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  // Table headings - left aligned and in required order
  // Standardized columns: Removed Discount. Added Items. Renamed Gross Revenue → Sales
  const headings = [
    { title: 'Order', alignment: 'start' },
    { title: 'Date', alignment: 'start' },
    { title: 'Brand', alignment: 'start' },
    { title: 'Marketplace', alignment: 'start' },
    { title: 'Status', alignment: 'start' },
    { title: 'Weight', alignment: 'start' },
    { title: 'Items', alignment: 'start' },
    { title: 'Sales', alignment: 'start' },
    { title: 'Refund', alignment: 'start' },
    { title: 'Net Sales', alignment: 'start' },
    { title: 'Commission', alignment: 'start' },
    { title: 'Shipping Fees', alignment: 'start' },
    { title: 'Return Fees', alignment: 'start' },
    { title: 'Net Payout', alignment: 'start' },
    { title: 'Actions', alignment: 'start' },
  ];

  // Table rows - includes both orders and refund line items (memoized for performance)
  const rowMarkup = useMemo(() => {
    const allRows = orders.flatMap((order, orderIndex) => {
    const rows = [];
    const getStatusTone = (status) => {
      switch (status) {
        case 'paid': return 'success';
        case 'pending': return 'attention';
        case 'cancelled': return 'critical';
        case 'refunded': return 'warning';
        case 'partially_refunded': return 'warning';
        default: return 'info';
      }
    };

    const getTone = (value) => {
      if (value < 0) return 'critical';
      if (value > 0) return 'success';
      return 'base';
    };

    // Add main order row
    rows.push(
      <IndexTable.Row
        id={order.id}
        key={order.id}
        selected={selectedResources.includes(order.id)}
        position={orderIndex}
        onClick={() => handleOrderSelect(order.id)}
      >
        {/* Order */}
        <IndexTable.Cell>
          <BlockStack gap="100">
            <Text variant="bodyMd" fontWeight="semibold">
              {order.orderNumber}
            </Text>
          </BlockStack>
        </IndexTable.Cell>

        {/* Date */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : 'N/A'}
          </Text>
        </IndexTable.Cell>

        {/* Brand */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {order.brand?.name || 'Unknown'}
          </Text>
        </IndexTable.Cell>

        {/* Marketplace */}
        <IndexTable.Cell>
          <Badge tone="info">
            {order.marketplace || 'Direct'}
          </Badge>
        </IndexTable.Cell>

        {/* Status */}
        <IndexTable.Cell>
          <Badge tone={getStatusTone(order.financialStatus)}>
            {order.financialStatus || 'Unknown'}
          </Badge>
        </IndexTable.Cell>

        {/* Weight */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.totalWeight ? apiClient.formatWeight(order.totalWeight) : '-'}
          </Text>
        </IndexTable.Cell>

        {/* Items (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.totalItems || 0}
          </Text>
        </IndexTable.Cell>

        {/* Sales (renamed from Gross Revenue - value is gross, discounts not subtracted) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.grossAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Refund*/}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.refundAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Net Sales */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium" tone="success">
            {apiClient.formatCurrency(order.netAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Commission */}
        <IndexTable.Cell>
          <BlockStack gap="100">
            <Text tone={getTone(order.appliedCommissionAmount)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedCommissionAmount)}
            </Text>
            <Text variant="bodySm" tone="subdued">
              {apiClient.formatPercentage(order.appliedCommissionRate || 0)}
            </Text>
          </BlockStack>
        </IndexTable.Cell>

        {/* Shipping Fees */}
        <IndexTable.Cell>
          <Text tone={getTone(order.appliedShippingFee || 0)} variant="bodyMd">
            {apiClient.formatCurrency(order.appliedShippingFee || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Return Fees */}
        <IndexTable.Cell>
          <Text tone={getTone(order.appliedReturnFee || 0)}  variant="bodyMd">
            {apiClient.formatCurrency(order.appliedReturnFee || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Net Payout */}
        <IndexTable.Cell>
          <Text tone={order.payout < 0 ? 'critical' : order.payout > 0 ? 'success' : 'base'} variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.payout || 0)}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <InlineStack gap="200">
            <Tooltip content="View order details">
              <Button
                variant="tertiary"
                size="micro"
                icon={ViewIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderSelect(order.id);
                }}
              />
            </Tooltip>
            {order.brand?.shopDomain && order.shopifyOrderId && (
              <Tooltip content="View in Shopify">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={ExternalIcon}
                  url={`https://${order.brand.shopDomain}/admin/orders/${order.shopifyOrderId}`}
                  external
                />
              </Tooltip>
            )}
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );

    // Add refund rows if order has refunds (matching backend billing service exactly)
    if (order.refunds && order.refunds.length > 0) {
      order.refunds.forEach((refund, refundIndex) => {
        const refundAmount = parseFloat(refund.refundAmount) || 0.0;
        const commissionRate = order.appliedCommissionRate || 0;
        
        // Backend calculations (matching billing-service.js)
        const refundCommissionAdjustment = refundAmount * commissionRate; // Merchant gets commission back
        const returnFeeApplied = refund.isPhysicalReturn ? (refund.returnFeeApplied || 0) : 0.0;

        rows.push(
          <IndexTable.Row
            id={`${order.id}-refund-${refund.id}`}
            key={`${order.id}-refund-${refund.id}`}
            selected={false}
            position={orderIndex + refundIndex + 1}
            tone="caution"
          >
            {/* Order */}
            <IndexTable.Cell>
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="medium" tone="caution">
                  {order.orderNumber} - REFUND
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {refund.reason || 'Refund processed'}
                </Text>
              </BlockStack>
            </IndexTable.Cell>

            {/* Date */}
            <IndexTable.Cell>
              <Text variant="bodyMd">
                {(refund.processedDate || refund.createdAt) ? new Date(refund.processedDate || refund.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                }) : 'N/A'}
              </Text>
            </IndexTable.Cell>

            {/* Brand */}
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="medium">
                {order.brand?.name || 'Unknown'}
              </Text>
            </IndexTable.Cell>

            {/* Marketplace */}
            <IndexTable.Cell>
              <Badge tone="caution">
                {order.marketplace || 'Direct'}
              </Badge>
            </IndexTable.Cell>

            {/* Status */}
            <IndexTable.Cell>
              <Badge tone="caution">
                refunded
              </Badge>
            </IndexTable.Cell>

            {/* Weight */}
            <IndexTable.Cell>
              <Text variant="bodyMd">-</Text>
            </IndexTable.Cell>

            {/* Items */}
            <IndexTable.Cell>
              <Text variant="bodyMd">-</Text>
            </IndexTable.Cell>

            {/* Sales - for refunds, show original order gross amount */}
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="medium">
                {apiClient.formatCurrency(order.grossAmount || 0)}
              </Text>
            </IndexTable.Cell>

            {/* Refund - show negative refund amount */}
            <IndexTable.Cell>
              <Text tone="critical" variant="bodyMd" fontWeight="medium">
                {apiClient.formatCurrency(-refundAmount)}
              </Text>
            </IndexTable.Cell>

            {/* Net Sales - calculated for the refund context */}
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="medium" tone="critical">
                {(() => {
                  const grossAmount = order.grossAmount || 0;
                  const discountAmount = order.discountAmount || 0;
                  const netSales = Math.max(0, grossAmount - discountAmount - refundAmount);
                  return apiClient.formatCurrency(netSales);
                })()}
              </Text>
            </IndexTable.Cell>

            {/* Shipping Fees - $0 for refunds */}
            <IndexTable.Cell>
              <Text variant="bodyMd">
                {apiClient.formatCurrency(0)}
              </Text>
            </IndexTable.Cell>

            {/* Return Fees - show return fee if applicable */}
            <IndexTable.Cell>
              <Text variant="bodyMd">
                {apiClient.formatCurrency(returnFeeApplied)}
              </Text>
            </IndexTable.Cell>

            {/* Commission - commission adjustment (merchant gets commission back) */}
            <IndexTable.Cell>
              <BlockStack gap="100">
                <Text tone="success" variant="bodyMd">
                  {apiClient.formatCurrency(-refundCommissionAdjustment)}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {apiClient.formatPercentage(commissionRate)}
                </Text>
              </BlockStack>
            </IndexTable.Cell>

            {/* Payout - use payout from API */}
            <IndexTable.Cell>
              <Text tone={order.payout < 0 ? 'critical' : order.payout > 0 ? 'success' : 'base'} variant="bodyMd" fontWeight="medium">
                {apiClient.formatCurrency(order.payout || 0)}
              </Text>
            </IndexTable.Cell>

            <IndexTable.Cell>
              <Badge tone="caution" size="small">
                Refund
              </Badge>
            </IndexTable.Cell>
          </IndexTable.Row>
        );
      });
    }

      return rows;
    });

    // Add summary row at the end if it exists
    if (summaryRow) {
      const summaryRowMarkup = (
        <IndexTable.Row
          id="SUMMARY_ROW"
          key="SUMMARY_ROW"
          selected={false}
          position={allRows.length}
          tone="subdued"
        >
          {/* Order - TOTAL label */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              TOTAL
            </Text>
          </IndexTable.Cell>

          {/* Date - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Brand - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Marketplace - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Status - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {summaryRow.totalWeight ? apiClient.formatWeight(summaryRow.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {summaryRow.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Amount) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refund */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.netAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedCommissionAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedShippingFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Return Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" tone={summaryRow.payout < 0 ? 'critical' : summaryRow.payout > 0 ? 'success' : 'base'}>
              {apiClient.formatCurrency(summaryRow.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return [...allRows, summaryRowMarkup];
    }

    return allRows;
  }, [orders, summaryRow, selectedResources, handleOrderSelect]);

  const emptyStateMarkup = (
    <EmptyState
      heading="No orders found"
      content={
        appliedFilters.length > 0
          ? "Try adjusting your search or filter criteria to find orders."
          : "Orders will appear here once they're synced from connected brands."
      }
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
      action={appliedFilters.length > 0 ? {
        content: 'Clear filters',
        onAction: handleFiltersClearAll,
      } : undefined}
    />
  );

  if (brandsLoading || (ordersLoading && currentPage === 1)) {
    return (
      <Page title="Order Management" fullWidth backAction={{ content: 'Dashboard', url: '/' }}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading orders...
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Fetching order data across all brands
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (ordersError) {
    return (
      <Page title="Order Management" fullWidth backAction={{ content: 'Dashboard', url: '/' }}>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load orders. {ordersError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Order Management"
      fullWidth
      subtitle={totalItems > 0 ? `${totalItems.toLocaleString()} orders` : undefined}
      backAction={{ content: 'Dashboard', url: '/' }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: () => refetchOrders(),
          loading: ordersLoading,
          helpText: 'Refresh order data to get the latest information',
        },
      ]}
      compactTitle
    >
      <Layout>
        {/* Orders Table */}
        <Layout.Section>
          <Card>
            {/* Filters integrated into table card */}
            <Box padding="200">
              <Filters
                queryValue={searchQuery}
                queryPlaceholder="Search orders..."
                onQueryChange={setSearchQuery}
                onQueryClear={() => setSearchQuery('')}
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={handleFiltersClearAll}
                disabled={ordersLoading}
              />
            </Box>
            <Divider />
            <IndexTable
              resourceName={resourceName}
              itemCount={rowMarkup.length}
              // selectedItemsCount={
              //   allResourcesSelected ? 'All' : selectedResources.length
              // }
              // onSelectionChange={handleSelectionChange}
              headings={headings}
              emptyState={emptyStateMarkup}
              loading={ordersLoading}
              selectable={false}
              sortable={[true, true, true, true, true, true, true, true, true, true, true, true, true, true, false]}
              sortDirection={sortDirection}
              sortColumnIndex={Math.max(0, headings.findIndex(h =>
                h?.title?.toLowerCase().includes(sortBy?.toLowerCase()) ||
                (sortBy === 'orderNumber' && h?.title === 'Order') ||
                (sortBy === 'orderDate' && h?.title === 'Date') ||
                (sortBy === 'financialStatus' && h?.title === 'Status') ||
                (sortBy === 'totalWeight' && h?.title === 'Weight') ||
                (sortBy === 'totalItems' && h?.title === 'Items') ||
                (sortBy === 'grossAmount' && h?.title === 'Sales') ||
                (sortBy === 'refundAmount' && h?.title === 'Refund') ||
                (sortBy === 'netAmount' && h?.title === 'Net Sales') ||
                (sortBy === 'appliedCommissionRate' && h?.title === 'Commission') ||
                (sortBy === 'appliedShippingFee' && h?.title === 'Shipping Fees') ||
                (sortBy === 'appliedReturnFee' && h?.title === 'Return Fees') ||
                (sortBy === 'payout' && h?.title === 'Net Payout')
              ))}
              onSort={handleSort}
              hasZebraStriping={true}
            >
              {rowMarkup}
            </IndexTable>

            {/* Table Footer with Summary and Pagination */}
            {(!ordersLoading && orders.length > 0) && (
              <>
                <Divider />
                <Box padding="400">
                  <InlineStack gap="800" align="space-between" blockAlign="center">
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box style={{flexShrink: 0}}>
                        <InlineStack gap="200" blockAlign="center">
                          <Button
                            disabled={currentPage <= 1}
                            onClick={handlePreviousPage}
                            size="slim"
                          >
                            Previous
                          </Button>
                          <Text variant="bodySm" tone="subdued">
                            {currentPage} of {totalPages}
                          </Text>
                          <Button
                            disabled={currentPage >= totalPages}
                            onClick={handleNextPage}
                            size="slim"
                          >
                            Next
                          </Button>
                        </InlineStack>
                      </Box>
                    )}
                  </InlineStack>
                </Box>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Order Details Modal */}
      {selectedOrder && (
        <Modal
          open={orderDetailsModalActive}
          onClose={() => setOrderDetailsModalActive(false)}
          title={`Order Details - ${selectedOrder.orderNumber}`}
          primaryAction={
            selectedOrder.brand?.shopDomain && selectedOrder.shopifyOrderId ? {
              content: 'View in Shopify',
              url: `https://${selectedOrder.brand.shopDomain}/admin/orders/${selectedOrder.shopifyOrderId}`,
              external: true,
            } : undefined
          }
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setOrderDetailsModalActive(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {/* Order Header */}
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="200">
                  <Text variant="headingMd">{selectedOrder.orderNumber}</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedOrder.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Date unavailable'}
                  </Text>
                </BlockStack>
                <Badge tone={
                  selectedOrder.financialStatus === 'paid' ? 'success' :
                  selectedOrder.financialStatus === 'pending' ? 'attention' :
                  selectedOrder.financialStatus === 'cancelled' ? 'critical' : 'warning'
                }>
                  {selectedOrder.financialStatus || 'Unknown'}
                </Badge>
              </InlineStack>

              <Divider />

              {/* Order Details */}
              <InlineStack gap="600" wrap={false}>
                <BlockStack gap="300">
                  <Text variant="headingSm">Customer & Brand</Text>
                  <BlockStack gap="100">
                    <Text variant="bodySm">
                      <strong>Customer:</strong> {selectedOrder.customerEmail || 'N/A'}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Brand:</strong> {selectedOrder.brand?.name || 'Unknown'}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Marketplace:</strong> {selectedOrder.marketplace || 'Direct'}
                    </Text>
                  </BlockStack>
                </BlockStack>

                <BlockStack gap="300">
                  <Text variant="headingSm">Order Info</Text>
                  <BlockStack gap="100">
                    <Text variant="bodySm">
                      <strong>Fulfillment:</strong> {selectedOrder.fulfillmentStatus || 'N/A'}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Weight:</strong> {selectedOrder.totalWeight ? apiClient.formatWeight(selectedOrder.totalWeight) : 'N/A'}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Tags:</strong> {selectedOrder.orderTags?.length > 0 ? selectedOrder.orderTags.join(', ') : 'None'}
                    </Text>
                  </BlockStack>
                </BlockStack>
              </InlineStack>

              <Divider />

              {/* Financial Details */}
              <BlockStack gap="300">
                <Text variant="headingSm">Financial Breakdown</Text>
                <InlineStack gap="600" wrap={false}>
                  <BlockStack gap="100">
                    <Text variant="bodySm">
                      <strong>Gross Amount:</strong> {apiClient.formatCurrency(selectedOrder.grossAmount || 0)}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Refunds:</strong> {apiClient.formatCurrency(selectedOrder.refundAmount || 0)}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Net Sales:</strong> {(() => {
                        const grossAmount = selectedOrder.grossAmount || 0;
                        const discountAmount = selectedOrder.discountAmount || 0;
                        const refundAmount = selectedOrder.refundAmount || 0;
                        const netSales = Math.max(0, grossAmount - discountAmount - refundAmount);
                        return apiClient.formatCurrency(netSales);
                      })()}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Net Amount:</strong> {apiClient.formatCurrency(selectedOrder.netAmount || 0)}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Return Fee:</strong> {apiClient.formatCurrency(selectedOrder.appliedReturnFee || 0)}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm">
                      <strong>Commission Rate:</strong> {apiClient.formatPercentage(selectedOrder.appliedCommissionRate || 0)}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Commission:</strong> {apiClient.formatCurrency(selectedOrder.appliedCommissionAmount || 0)}
                    </Text>
                    <Text variant="bodySm">
                      <strong>Shipping Fee:</strong> {apiClient.formatCurrency(selectedOrder.appliedShippingFee || 0)}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}